/**
 * CookieConsent — GDPR analytics-consent banner for the public marketing site.
 *
 * Microsoft Clarity uses cookies, so it must not run until the visitor
 * accepts analytics. This banner appears only when no prior choice exists
 * and gates Clarity initialization via config/clarity.ts.
 *
 * Scope: marketing site only (anonymous visitors). The authenticated app
 * uses Application Insights under the existing privacy policy.
 */
import React, { useState } from 'react';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { marketingPalette as legacyPalette, marketingFonts } from '../theme';
import { colors } from '../public/design';
import {
  getStoredConsent,
  grantAnalyticsConsent,
  denyAnalyticsConsent,
} from '../../../config/clarity';

interface CookieConsentProps {
  /** Open the privacy policy sub-page. */
  onOpenPrivacy?: () => void;
  appearance?: 'legacy' | 'public';
  language?: 'sv' | 'en';
}

export const CookieConsent: React.FC<CookieConsentProps> = ({ onOpenPrivacy, appearance = 'legacy', language = 'sv' }) => {
  const marketingPalette = appearance === 'public' ? {
    ...legacyPalette, ctaBg: colors.graphite, accent: colors.rust, accentHover: colors.rustDark,
  } : legacyPalette;
  const [visible, setVisible] = useState<boolean>(() => getStoredConsent() === null);

  if (!visible) {
    return null;
  }

  const handleAccept = () => {
    grantAnalyticsConsent();
    setVisible(false);
  };

  const handleDecline = () => {
    denyAnalyticsConsent();
    setVisible(false);
  };

  return (
    <Box
      role="dialog"
      aria-label={language === 'sv' ? 'Samtycke till analyscookies' : 'Analytics cookie consent'}
      sx={{
        position: 'fixed',
        bottom: { xs: 0, sm: 24 },
        left: { xs: 0, sm: 24 },
        right: { xs: 0, sm: 'auto' },
        zIndex: 1400,
        maxWidth: { xs: '100%', sm: 420 },
        bgcolor: marketingPalette.ctaBg,
        color: marketingPalette.ctaText,
        fontFamily: appearance === 'public' ? 'Arial, Helvetica, sans-serif' : marketingFonts.sans,
        borderRadius: { xs: 0, sm: 2 },
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
        p: 3,
      }}
    >
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 1 }}>
        {language === 'sv' ? 'Vi använder cookies för analys' : 'We use cookies for analytics'}
      </Typography>
      <Typography
        sx={{ fontSize: '0.85rem', lineHeight: 1.6, color: marketingPalette.ctaTextMuted, mb: 2 }}
      >
        {language === 'sv'
          ? 'Vi använder Microsoft Clarity för anonym besöksstatistik så att vi kan förbättra webbplatsen. Inga cookies sätts förrän du godkänner. '
          : 'We use Microsoft Clarity for anonymous visitor statistics so we can improve the website. No cookies are set until you accept. '}
        {onOpenPrivacy && (
          <Link
            component="button"
            type="button"
            onClick={onOpenPrivacy}
            sx={{ color: appearance === 'public' ? colors.copper : marketingPalette.accent, textDecorationColor: 'currentColor' }}
          >
            {language === 'sv' ? 'Läs mer i integritetspolicyn' : 'Read more in the privacy policy'}
          </Link>
        )}
      </Typography>
      <Stack direction="row" spacing={1.5}>
        <Button
          onClick={handleAccept}
          variant="contained"
          sx={{
            bgcolor: marketingPalette.accent,
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { bgcolor: marketingPalette.accentHover },
          }}
        >
          {language === 'sv' ? 'Godkänn' : 'Accept'}
        </Button>
        <Button
          onClick={handleDecline}
          variant="text"
          sx={{
            color: marketingPalette.ctaTextMuted,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { color: marketingPalette.ctaText, bgcolor: 'rgba(255,255,255,0.06)' },
          }}
        >
          {language === 'sv' ? 'Avböj' : 'Decline'}
        </Button>
      </Stack>
    </Box>
  );
};
